import {
  Button,
  Divider,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
} from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  type PersonDto,
  type SavedReportDto,
  useShareSavedReportMutation,
  useStopSharingSavedReportMutation,
} from "../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../common/auth/useLoggedInWithOrgContextState";
import { isNotFoundError } from "../../common/errors/isNotFoundError";
import { useApiError } from "../../common/errors/useApiError";
import { PersonSelector } from "../../common/persons/PersonSelector";
import { personName } from "../../common/components/communication/communicationUtils";

export const ShareReportModal = ({ report, onClose }: { report: SavedReportDto; onClose: () => void }) => {
  const { t } = useTranslation();
  const { me, selectedOrg } = useLoggedInWithOrgContextState();
  const { trigger } = useApiError();
  const [selected, setSelected] = useState<PersonDto | null>(null);
  const [removingPersonId, setRemovingPersonId] = useState<string>();
  const [share, { isLoading: isSharing }] = useShareSavedReportMutation();
  const [stopSharing] = useStopSharingSavedReportMutation();

  const onShare = async () => {
    if (!selected) return;
    try {
      await share({ savedReportId: report.id, shareSavedReportRequest: { personId: selected.id } }).unwrap();
      setSelected(null);
    } catch (e) {
      if (isNotFoundError(e)) onClose();
      trigger(e);
    }
  };

  const isWriting = isSharing || removingPersonId !== undefined;

  const onStopSharing = async (person: PersonDto) => {
    setRemovingPersonId(person.id);
    try {
      await stopSharing({ savedReportId: report.id, pathPersonId: person.id }).unwrap();
    } catch (e) {
      if (isNotFoundError(e)) onClose();
      trigger(e);
    } finally {
      setRemovingPersonId(undefined);
    }
  };

  return (
    <Modal isOpen onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t("Share «{{name}}»", { name: report.name })}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack spacing="4">
            <Text fontSize="sm" color="smMuted">
              {t(
                "A colleague you share with sees the same filter, columns and sorting, but only the rows they have access to. They cannot change or delete the report."
              )}
            </Text>
            <HStack align="flex-start">
              <Stack flex="1">
                <PersonSelector
                  size="md"
                  organizationIds={[selectedOrg.id]}
                  selectedPerson={selected}
                  onChange={setSelected}
                  personsToExclude={[...report.sharedWith.map((p) => p.id), me.id]}
                  placeholder={t("Share with a colleague") ?? ""}
                />
              </Stack>
              <Button colorScheme="teal" onClick={() => void onShare()} isLoading={isSharing} isDisabled={!selected || isWriting}>
                {t("Share")}
              </Button>
            </HStack>
            <Divider />
            {report.sharedWith.length === 0 ? (
              <Text fontSize="sm" color="smMuted">
                {t("Not shared with anyone yet.")}
              </Text>
            ) : (
              report.sharedWith.map((person) => (
                <HStack key={person.id} justify="space-between" align="flex-start">
                  <Stack spacing="0">
                    <Text fontWeight="semibold">{personName(person)}</Text>
                    <Text fontSize="sm" color="smMuted">
                      {person.email}
                    </Text>
                  </Stack>
                  <Button
                    size="sm"
                    variant="outline"
                    colorScheme="red"
                    isLoading={removingPersonId === person.id}
                    isDisabled={isWriting}
                    onClick={() => void onStopSharing(person)}
                  >
                    {t("Remove")}
                  </Button>
                </HStack>
              ))
            )}
          </Stack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={onClose}>
            {t("Close")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
